import { tokens } from '@/lib/tokens.js';
import { useState } from 'react';
import {
  useAddRoleMember,
  useBatchRoleMembers,
  useRemoveRoleMember,
  useRoleMembers,
} from '../../hooks/use-admin-role-members.js';

const s = {
  container: { display: 'flex', flexDirection: 'column' as const, gap: '1rem' },
  toolbar: {
    display: 'flex',
    gap: '0.75rem',
    alignItems: 'center',
  },
  input: {
    backgroundColor: '#0f172a',
    border: `1px solid ${tokens.colors.borderDark}`,
    borderRadius: tokens.radius.input,
    color: tokens.colors.textDark,
    padding: '0.375rem 0.75rem',
    fontSize: '0.875rem',
    outline: 'none',
    fontFamily: 'inherit',
    width: '240px',
  },
  btn: {
    backgroundColor: tokens.colors.primary,
    color: '#fff',
    border: 'none',
    borderRadius: tokens.radius.button,
    padding: '0.375rem 0.875rem',
    fontSize: '0.875rem',
    cursor: 'pointer',
    fontWeight: 500,
  },
  btnSm: {
    backgroundColor: 'transparent',
    color: tokens.colors.error,
    border: `1px solid ${tokens.colors.error}`,
    borderRadius: tokens.radius.button,
    padding: '0.25rem 0.5rem',
    fontSize: '0.75rem',
    cursor: 'pointer',
  },
  table: { width: '100%', borderCollapse: 'collapse' as const },
  th: {
    padding: '0.625rem 0.75rem',
    textAlign: 'left' as const,
    fontSize: '0.75rem',
    fontWeight: 600,
    color: tokens.colors.textMutedDark,
    textTransform: 'uppercase' as const,
    borderBottom: `1px solid ${tokens.colors.borderDark}`,
  },
  td: {
    padding: '0.625rem 0.75rem',
    fontSize: '0.875rem',
    color: tokens.colors.textDark,
    borderBottom: '1px solid rgba(55,65,81,0.4)',
  },
};

interface MembersTabProps {
  tenantId: string;
  roleId: string;
}

export function MembersTab({ tenantId, roleId }: MembersTabProps) {
  const [newUserId, setNewUserId] = useState('');
  const [batchText, setBatchText] = useState('');
  const [batchMode, setBatchMode] = useState(false);

  const { data, isLoading } = useRoleMembers(tenantId, roleId);
  const addMember = useAddRoleMember(tenantId, roleId);
  const removeMember = useRemoveRoleMember(tenantId, roleId);
  const batchMembers = useBatchRoleMembers(tenantId, roleId);

  function handleAdd() {
    if (!newUserId.trim()) return;
    addMember.mutate({ user_id: newUserId.trim() }, { onSuccess: () => setNewUserId('') });
  }

  function handleBatchAdd() {
    const ids = batchText
      .split(/[\n,]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    batchMembers.mutate({ add: ids }, { onSuccess: () => setBatchText('') });
  }

  const members = data?.items ?? [];

  return (
    <div style={s.container}>
      {!batchMode ? (
        <div style={s.toolbar}>
          <input
            style={s.input}
            value={newUserId}
            onChange={(e) => setNewUserId(e.target.value)}
            placeholder="User ID to add…"
            aria-label="User ID"
          />
          <button
            type="button"
            style={s.btn}
            onClick={handleAdd}
            disabled={addMember.isPending}
            aria-label="Add member"
          >
            {addMember.isPending ? 'Adding…' : 'Add'}
          </button>
          <button
            type="button"
            style={{
              ...s.btn,
              backgroundColor: 'transparent',
              color: tokens.colors.textMutedDark,
              border: `1px solid ${tokens.colors.borderDark}`,
            }}
            onClick={() => setBatchMode(true)}
            aria-label="Batch add members"
          >
            Batch…
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <textarea
            style={{
              ...s.input,
              width: '100%',
              minHeight: '80px',
              resize: 'vertical',
              fontFamily: 'monospace',
              boxSizing: 'border-box',
            }}
            value={batchText}
            onChange={(e) => setBatchText(e.target.value)}
            placeholder="User IDs — one per line or comma-separated"
            aria-label="Batch user IDs"
          />
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              type="button"
              style={s.btn}
              onClick={handleBatchAdd}
              disabled={batchMembers.isPending}
              aria-label="Apply batch add"
            >
              {batchMembers.isPending ? 'Applying…' : 'Apply Add'}
            </button>
            <button
              type="button"
              style={{
                ...s.btn,
                backgroundColor: 'transparent',
                color: tokens.colors.textMutedDark,
                border: `1px solid ${tokens.colors.borderDark}`,
              }}
              onClick={() => setBatchMode(false)}
            >
              Cancel
            </button>
          </div>
          {batchMembers.isSuccess && (
            <output style={{ display: 'block', color: '#86efac', fontSize: '0.875rem' }}>
              Added {(batchMembers.data as { added: number }).added}, removed{' '}
              {(batchMembers.data as { removed: number }).removed}
            </output>
          )}
        </div>
      )}

      {addMember.isError && (
        <p style={{ color: tokens.colors.error, fontSize: '0.875rem' }} role="alert">
          Error adding member. If the member was added by a rule, disable the rule first.
        </p>
      )}

      {isLoading ? (
        <output
          style={{ display: 'block', color: tokens.colors.textMutedDark, fontSize: '0.875rem' }}
          aria-label="Loading members"
        >
          Loading…
        </output>
      ) : (
        <table style={s.table} aria-label="Role members">
          <thead>
            <tr>
              <th style={s.th}>Email</th>
              <th style={s.th}>Display name</th>
              <th style={s.th}>Source</th>
              <th style={s.th}>Added</th>
              <th style={s.th} />
            </tr>
          </thead>
          <tbody>
            {members.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  style={{ ...s.td, textAlign: 'center', color: tokens.colors.textMutedDark }}
                >
                  No members
                </td>
              </tr>
            ) : (
              members.map((m) => (
                <tr key={m.id}>
                  <td style={s.td}>{m.user_email}</td>
                  <td style={s.td}>{m.user_display_name ?? '—'}</td>
                  <td style={s.td}>
                    <span
                      style={{
                        fontFamily: 'monospace',
                        fontSize: '0.8125rem',
                        color: m.source === 'rule' ? '#c084fc' : '#93c5fd',
                      }}
                    >
                      {m.source}
                    </span>
                  </td>
                  <td style={{ ...s.td, color: tokens.colors.textMutedDark }}>
                    {new Date(m.added_at).toLocaleDateString()}
                  </td>
                  <td style={s.td}>
                    {m.source === 'manual' && (
                      <button
                        type="button"
                        style={s.btnSm}
                        onClick={() => removeMember.mutate(m.id)}
                        aria-label={`Remove ${m.user_email}`}
                      >
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}
